const CODESTITCH_BASELINE_MARKER = 'Page Builder CodeStitch baseline';

export const CODESTITCH_BASELINE_CSS = `/* ${CODESTITCH_BASELINE_MARKER} */
.cs-button-solid{font-size:1rem;line-height:clamp(2.875rem,5.5vw,3.5rem);text-decoration:none;font-weight:700;text-align:center;width:auto;min-width:9.375rem;margin:0;padding:0 1.5rem;background-color:var(--primary);color:#fff;border-radius:.25rem;display:inline-block;position:relative;z-index:1;box-sizing:border-box;transition:color .3s,background-color .3s,border-color .3s}
.cs-button-solid:hover{cursor:pointer}
.cs-button-transparent,.cs-button-outline,.button-transparent,.cs-button-solid.cs-outline{font-size:1rem;line-height:clamp(2.875rem,5.5vw,3.5rem);text-decoration:none;font-weight:700;text-align:center;width:auto;min-width:9.375rem;margin:0;padding:0 1.5rem;background-color:transparent;color:var(--primary);border:1px solid currentColor;border-radius:.25rem;display:inline-block;position:relative;z-index:1;box-sizing:border-box;transition:color .3s,background-color .3s,border-color .3s}
.cs-button-transparent:hover,.cs-button-outline:hover,.button-transparent:hover,.cs-button-solid.cs-outline:hover{cursor:pointer;background-color:var(--primary);border-color:var(--primary);color:#fff}
.cs-button-secondary,.cs-button-solid.cs-secondary{font-size:1rem;line-height:clamp(2.875rem,5.5vw,3.5rem);text-decoration:none;font-weight:700;text-align:center;width:auto;min-width:9.375rem;margin:0;padding:0 1.5rem;background-color:var(--secondary);color:#fff;border-radius:.25rem;display:inline-block;position:relative;z-index:1;box-sizing:border-box;transition:color .3s,background-color .3s,border-color .3s}
.cs-button-secondary:hover,.cs-button-solid.cs-secondary:hover{cursor:pointer;background-color:var(--primary)}

body.dark-mode{background-color:var(--dark, #082032)}
body.dark-mode p,body.dark-mode li,body.dark-mode h1,body.dark-mode h2,body.dark-mode h3,body.dark-mode h4,body.dark-mode h5,body.dark-mode h6{color:#fff}
body.dark-mode .cs-topper{color:var(--primary)}
.cs-dark,.dark{display:none}
body.dark-mode .cs-light,body.dark-mode .light{display:none}
body.dark-mode .cs-dark,body.dark-mode .dark{display:block!important}

@media only screen and (min-width:0em){
  body.dark-mode [id^="dark-mode-toggle"] .cs-sun{transform:translate(-50%,-50%);opacity:1}
  body.dark-mode [id^="dark-mode-toggle"] .cs-moon{transform:translate(-50%,-150%);opacity:0;fill:#fff}
  [id^="dark-mode-toggle"]{display:block;position:absolute;top:50%;right:3.75rem;transform:translateY(-50%);width:3rem;height:3rem;background:transparent;border:none;overflow:hidden;padding:0;z-index:1000}
  [id^="dark-mode-toggle"] img,[id^="dark-mode-toggle"] svg{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:1.5625rem;height:1.5625rem;pointer-events:none}
  [id^="dark-mode-toggle"] .cs-moon{z-index:2;transition:transform .3s,opacity .3s,fill .3s}
  [id^="dark-mode-toggle"] .cs-sun{z-index:1;transform:translate(-50%,100%);opacity:0;transition:transform .3s,opacity .3s}
  .cs-hide-on-mobile{display:none!important}
}
@media only screen and (min-width:64rem){
  [id^="dark-mode-toggle"]{position:relative;top:auto;right:auto;transform:none;margin-left:1.875rem;margin-bottom:0}
  [id^="dark-mode-toggle"]:hover{cursor:pointer}
  .cs-hide-on-mobile{display:initial!important}
  .cs-hide-on-desktop{display:none!important}
}`;

export function withCodeStitchBaselineCSS(css = '') {
  const rawCSS = typeof css === 'string' ? css : '';
  if (rawCSS.includes(CODESTITCH_BASELINE_MARKER)) return rawCSS;
  const baseCSS = rawCSS.trimEnd();
  return [baseCSS, CODESTITCH_BASELINE_CSS].filter(Boolean).join('\n\n');
}

/* ══════════════════════════════════════════════════════════════════════════
   RECLAIM — giving a site back a class the baseline hides on sight.
   ══════════════════════════════════════════════════════════════════════════ */

const RECLAIM_MARKER = 'Page Builder baseline reclaim';

/**
 * Class tokens the baseline hides unconditionally, and which are ALSO ordinary
 * English words a hand-coded site will reach for.
 *
 * `.cs-dark` / `.cs-light` are namespaced and safe. The BARE pair is not: the
 * baseline hides `.dark` because CodeStitch ships light/dark image pairs that
 * way, and 88 crawled sites depend on exactly that. But 65 others use `.dark`
 * as their own element — abshillbillygyros.com's hero overlay is
 * `.dark{position:absolute;height:100%;width:100%;background:#000;opacity:.7}`,
 * a semi-transparent scrim — and `display:none` deletes it. Same specificity as
 * the site's own rule and the only one declaring `display`, so source order
 * cannot save it and the site's headline washes out on the bare photo.
 */
const RECLAIMABLE_CLASSES = Object.freeze(['dark', 'light']);

/**
 * Properties that make a class a REAL ELEMENT rather than a swap target.
 *
 * This is the whole discrimination, and it is deliberately narrow. A CodeStitch
 * dark-mode variant is a second `<img>`/`<svg>` that the toggle swaps in; it is
 * never given a position, a paint or an opacity, because it occupies the same
 * box as its sibling. A scrim, a backdrop or a tinted panel always is. Widening
 * this list trades a fixed 65-site win for the risk of un-hiding a genuine
 * variant on one of the 88, so it should only ever shrink.
 */
const OWN_ELEMENT_PROPS = Object.freeze([
  'position',
  'background',
  'background-color',
  'background-image',
  'opacity',
  'z-index',
  'inset'
]);

/** Every declaration block whose selector targets the BARE class, unqualified. */
function bareClassBlocks(css, token) {
  const out = [];
  // `.dark` as a WHOLE TOKEN, and TWO characters do all the work:
  //
  //   · the literal `\.` excludes `.cs-dark`, `.menu-strip-dark` and every
  //     other `-dark` suffix — none of them contains the substring `.dark`;
  //   · `(?![\w-])` excludes `.darkroom` and `.dark-mode`.
  //
  // There is deliberately NO leading guard. A first draft had `(^|[^\w.-])`,
  // and a seed proved it was doing something other than advertised: it did not
  // protect `-dark` (the `\.` already does), it silently EXCLUDED compound
  // selectors like `.hero.dark`, which are a site using the bare class exactly
  // as much as a lone `.dark` is. A `.dark` substring cannot occur anywhere but
  // at the head of a class token, so the guard bought nothing and cost matches.
  const re = new RegExp(`\\.${token}(?![\\w-])[^{}]*\\{([^}]*)\\}`, 'g');
  let m;
  while ((m = re.exec(css))) {
    const selectorStart = css.lastIndexOf('}', m.index) + 1;
    const selector = css.slice(selectorStart, css.indexOf('{', m.index));
    // A rule already scoped to the dark-mode body IS the baseline's own
    // convention being used correctly; reclaiming it would break the toggle.
    if (/\bdark-mode\b/.test(selector)) continue;
    out.push({ selector: selector.trim(), body: m[1] });
  }
  return out;
}

function declaresProp(body, prop) {
  return new RegExp(`(^|;)\\s*${prop}\\s*:`, 'i').test(body);
}

/**
 * The classes this site uses as its OWN elements, and which the baseline would
 * therefore wrongly hide.
 *
 * A site that declares `display` on the bare class has stated its own intent
 * and is left alone — including a site that agrees with the baseline and hides
 * it deliberately.
 *
 * @param {string} siteCSS the site's FULL stylesheet, before splitting
 * @returns {string[]} reclaimable tokens, in `RECLAIMABLE_CLASSES` order
 */
export function reclaimableBaselineClasses(siteCSS = '') {
  const css = typeof siteCSS === 'string' ? siteCSS : '';
  if (!css) return [];
  const found = [];
  for (const token of RECLAIMABLE_CLASSES) {
    const blocks = bareClassBlocks(css, token);
    if (!blocks.length) continue;
    if (blocks.some(({ body }) => declaresProp(body, 'display'))) continue;
    const ownsIt = blocks.some(({ body }) =>
      OWN_ELEMENT_PROPS.some((prop) => declaresProp(body, prop))
    );
    if (ownsIt) found.push(token);
  }
  return found;
}

/**
 * The override block, or '' when this site needs none.
 *
 * `display: revert` rather than `block`: the element goes back to whatever its
 * tag would be, so a reclaimed `<span class="dark">` stays inline. Appended to
 * the SITE's `_globals.css`, which the canvas emits AFTER `global-base.css` —
 * so equal specificity plus source order is enough and no `!important` is
 * needed. An `!important` here would beat the baseline's own
 * `body.dark-mode .dark{display:block!important}` and break the toggle for any
 * site that uses both.
 */
export function baselineReclaimCSS(siteCSS = '') {
  const tokens = reclaimableBaselineClasses(siteCSS);
  if (!tokens.length) return '';
  const selector = tokens.map((t) => `.${t}`).join(',');
  return `/* ${RECLAIM_MARKER} */\n${selector}{display:revert}`;
}

/** Append the reclaim block to a site's globals residue. */
export function withBaselineReclaimCSS(globalsCSS = '', siteCSS = '') {
  const base = typeof globalsCSS === 'string' ? globalsCSS : '';
  if (base.includes(RECLAIM_MARKER)) return base;
  const reclaim = baselineReclaimCSS(siteCSS);
  if (!reclaim) return base;
  return [base.trimEnd(), reclaim].filter(Boolean).join('\n\n') + '\n';
}
